<template>
	<button
			class="app-btn"
			:class="{ 'app-disabled': app.disabled, 'is-active': isActive }"
			@click="debouncedClick"
			:disabled="disabled || (isActive && !requiresGameSelect)"
			:title="app.label"
	>
		<AppIcon :name="app.icon_url" class="app-icon app-icon-fill"/>
		<span v-if="app.status && app.status.toLowerCase() !== 'prd'" class="env-badge">{{ app.status }}</span>
		<span v-if="isActive" class="running-badge">Running</span>
		<span v-if="app.disabled" class="disabled-badge">{{ app.disabled_reason }}</span>
	</button>
</template>

<script setup>
import {computed, ref} from 'vue'
import AppIcon from './AppIcon.vue'

const DEBOUNCE_MS = 600
const lastClickTime = ref(0)

const props = defineProps({
	app: {type: Object, required: true},
	disabled: {type: Boolean, default: false},
	isActive: {type: Boolean, default: false},
})

const isActive = computed(() => props.isActive)
const requiresGameSelect = computed(() => {
	const gtype = props.app?.game_id_type
	return !!gtype && gtype !== 'all'
})

const emit = defineEmits(['click'])

function debouncedClick() {
	if (props.app.disabled || (isActive && !requiresGameSelect)) return
	const now = Date.now()
	if (now - lastClickTime.value < DEBOUNCE_MS) return
	lastClickTime.value = now
	emit('click')
}
</script>

<style scoped>
.app-btn {
	position: relative;
	aspect-ratio: 1 / 1;
	padding: 0;
	background: var(--color-popup-element);
	border: var(--border-thin) var(--color-border);
	border-radius: var(--border-radius-md);
	cursor: pointer;
	transition: all var(--transition-fast);
	overflow: hidden;
}

.app-btn:disabled, .app-btn.app-disabled > .app-icon {
	opacity: 0.5;
	filter: brightness(0.6);
	cursor: not-allowed;
}

.app-btn.is-active {
	filter: none;
}

.app-btn.is-active::after {
	content: '';
	color: var(--color-text);
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	inset: 0;
	box-shadow: 0 0 var(--spacing-xl) var(--color-primary) inset;
	pointer-events: none;
}

.app-btn.is-active:disabled {
	opacity: 1;
	cursor: not-allowed !important;
}

.app-icon-fill {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: var(--border-radius-md);
}

.env-badge, .disabled-badge, .running-badge, .coming-soon-badge {
	position: absolute;
	top: var(--spacing-sm);
	right: var(--spacing-sm);
	padding: var(--spacing-xs);
	border-radius: var(--border-radius-sm);
	background: var(--gradient-alternate);
	color: var(--color-text);
	font-size: var(--font-size-sm);
	font-weight: var(--font-weight-semibold);
	text-shadow: var(--shadow-sm);
	text-transform: uppercase;
	letter-spacing: 0.05em;
	line-height: 1.2;
	white-space: nowrap;
	pointer-events: none;
	z-index: 2;
}

.disabled-badge {
	background: var(--gradient-secondary);
	width: calc(100% - (2 * var(--spacing-sm)));
	z-index: 1;
}

.running-badge {
	background: var(--gradient-primary-dark);
	width: calc(100% - (2 * var(--spacing-sm)));
	z-index: 1;
}
</style>